/**
 * DataTable — accessible table primitive for the admin sub-screens
 * (issue #433 acceptance criterion #6).
 *
 * Wraps the existing `.admin-table` styling and:
 *
 *   - Sticky <thead> on desktop viewports (so the column headers
 *     stay in view while scrolling a long list).
 *   - Optional row-level `aria-sort` for sortable columns.
 *   - Mobile collapse: when the consumer passes `mobileCard`, the
 *     table renders as a card list at <1024px via a CSS-only
 *     technique (rows are still <tr> elements; the CSS uses
 *     `display: block` per row to stack label/value pairs at
 *     narrow widths). This keeps screen-reader semantics intact.
 *
 * The component is intentionally untyped — column shape is
 * caller-defined. Consumers pass a stable `keyFor(row)` and a
 * `cells(row)` array of rendered cells in column order.
 */
import type { ReactElement, ReactNode } from 'react';
import { useBreakpoint } from '../lib/ui/useBreakpoint';

export interface DataTableColumn<TRow> {
  /** Header label. */
  header: string;
  /** Optional CSS class on every <th>/<td> in this column. */
  className?: string;
  /** Cell renderer. */
  cell: (row: TRow) => ReactNode;
  /** True if the column should mark its <th scope="row"> on the first column. */
  isRowHeader?: boolean;
}

export interface DataTableProps<TRow> {
  /**
   * Stable unique key per row. Receives the row and its index in
   * `rows` so callers whose source rows lack a true primary key
   * (e.g. paginated audit logs keyed by `accessed_at + accessed_by`
   * — both subject to clock-tick collisions) can still produce
   * unique keys without reaching for a non-stable workaround.
   */
  rowKey: (row: TRow, index: number) => string;
  /** Column definitions in render order. */
  columns: DataTableColumn<TRow>[];
  /** Rows. */
  rows: TRow[];
  /** Accessible caption rendered above the table; hidden visually by default. */
  caption?: string;
  /** Optional aria-label override; defaults to the caption. */
  ariaLabel?: string;
  /** Empty-state placeholder rendered when rows is empty. */
  emptyState?: ReactNode;
}

export function DataTable<TRow>({
  rowKey,
  columns,
  rows,
  caption,
  ariaLabel,
  emptyState,
}: DataTableProps<TRow>): ReactElement {
  const bp = useBreakpoint();

  if (rows.length === 0 && emptyState !== undefined) {
    return <>{emptyState}</>;
  }

  // Mark the column that owns the row header (first column by
  // convention). Callers can override via isRowHeader.
  const rowHeaderIndex = columns.findIndex((c) => c.isRowHeader === true);
  const effectiveRowHeaderIndex = rowHeaderIndex >= 0 ? rowHeaderIndex : 0;

  const isMobile = bp === 'mobile' || bp === 'tablet';

  return (
    <div className="data-table-wrapper" data-bp={bp}>
      <table
        className={`admin-table data-table${isMobile ? ' data-table--mobile' : ''}`}
        aria-label={ariaLabel ?? caption}
      >
        {caption !== undefined && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr>
            {columns.map((col, idx) => (
              <th
                key={`h-${idx}-${col.header}`}
                scope="col"
                className={col.className}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => {
            const key = rowKey(row, rowIdx);
            return (
            <tr key={key}>
              {columns.map((col, idx) => {
                const isRowHeader = idx === effectiveRowHeaderIndex;
                const cellContent = (
                  <>
                    {isMobile && (
                      <span className="data-table-mobile-label" aria-hidden="true">
                        {col.header}
                      </span>
                    )}
                    <span className="data-table-mobile-value">
                      {col.cell(row)}
                    </span>
                  </>
                );
                return isRowHeader ? (
                  <th
                    key={`c-${key}-${idx}`}
                    scope="row"
                    className={col.className}
                    data-label={col.header}
                  >
                    {cellContent}
                  </th>
                ) : (
                  <td
                    key={`c-${key}-${idx}`}
                    className={col.className}
                  >
                    {cellContent}
                  </td>
                );
              })}
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
